import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Mail, Loader2, CheckCircle, ExternalLink, Copy } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL
  ? process.env.REACT_APP_BACKEND_URL + "/api"
  : "/api";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { message, reset_url? }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Please enter your email address"); return; }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(result.reset_url);
    toast.success("Reset link copied!");
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <button
            onClick={() => navigate("/login")}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit mb-2"
            data-testid="back-to-login-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>
          <CardTitle className="text-2xl">Forgot password</CardTitle>
          <CardDescription>
            {result
              ? result.reset_url
                ? "Your reset link is ready — copy it and open it in your browser"
                : "Check your email for the reset link"
              : "Enter your email and we'll send you a password reset link"}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {!result ? (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    disabled={loading}
                    required
                    autoFocus
                    data-testid="forgot-password-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="forgot-password-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  "Send Reset Link"
                )}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Remember your password?{" "}
                <Link to="/login" className="text-primary hover:underline">Sign in</Link>
              </p>
            </form>

          ) : result.reset_url ? (
            /* Email not configured — show direct link */
            <div className="space-y-4" data-testid="reset-link-result">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <p className="text-sm text-amber-200 font-medium mb-1">Email service not configured</p>
                <p className="text-xs text-amber-200/70">
                  Your reset link is shown below. Click it or copy it to reset your password.
                  Expires in {result.expires_in}.
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-3 border border-border">
                <p className="text-xs text-muted-foreground mb-2">Reset link</p>
                <p className="text-xs text-primary break-all font-mono leading-relaxed" data-testid="reset-url-text">
                  {result.reset_url}
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  onClick={() => window.open(result.reset_url, '_self')}
                  data-testid="open-reset-link-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open reset link
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyLink}
                  data-testid="copy-reset-link-btn"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>

              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={() => { setResult(null); setEmail(""); }}
              >
                Try a different email
              </Button>
            </div>

          ) : (
            /* Email sent */
            <div className="space-y-4" data-testid="email-sent-result">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-green-200 font-medium">Check your email</p>
                  <p className="text-xs text-green-200/70 mt-1">
                    We sent a reset link to <strong>{email}</strong>. Check your inbox and spam folder.
                  </p>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => { setResult(null); setEmail(""); }}
              >
                Try a different email
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                <Link to="/login" className="text-primary hover:underline">Back to login</Link>
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
